import { Controller } from "@hotwired/stimulus"
import { DirectUpload } from "@rails/activestorage"

/**
 * Direct Upload Controller
 * 
 * Handles ActiveStorage direct uploads with visual progress feedback.
 * Manually triggers direct upload to work with Turbo navigation.
 */
export default class extends Controller {
  static targets = ["input", "preview", "progress", "progressBar", "progressText", "fileName"]
  static values = {
    imageType: { type: String, default: "image" },
    dimensions: { type: String, default: "" },
    saveUrl: { type: String, default: "" },
    fieldName: { type: String, default: "" }
  }

  connect() {
    this.bindEvents()
  }

  disconnect() {
    this.unbindEvents()
  }

  bindEvents() {
    if (this.hasInputTarget) {
      this.handleFileSelect = this.handleFileSelect.bind(this)
      this.inputTarget.addEventListener("change", this.handleFileSelect)
    }
  }

  unbindEvents() {
    if (this.hasInputTarget) {
      this.inputTarget.removeEventListener("change", this.handleFileSelect)
    }
  }

  handleFileSelect(event) {
    const file = event.target.files[0]
    if (!file) return

    console.log(`[DirectUpload] File selected: ${file.name}`)

    // Validate file type
    if (!file.type.startsWith("image/")) {
      this.showError("Please select a valid image file")
      event.target.value = ""
      return
    }

    // Validate file size (max 25MB)
    const maxSize = 25 * 1024 * 1024
    if (file.size > maxSize) {
      this.showError("Image is too large. Maximum size is 25MB.")
      event.target.value = ""
      return
    }

    // Show upload UI immediately
    this.showUploadUI(file)
    
    // Get the direct upload URL from the input
    const url = this.inputTarget.dataset.directUploadUrl
    if (!url) {
      console.error("[DirectUpload] No direct upload URL found!")
      this.showError("Upload configuration error. Please refresh and try again.")
      return
    }

    console.log(`[DirectUpload] Starting upload to: ${url}`)
    
    // Manually trigger the direct upload
    this.uploadFile(file, url)
  }

  uploadFile(file, url) {
    this.disableFormSubmit()
    
    const upload = new DirectUpload(file, url, {
      directUploadWillStoreFileWithXHR: (request) => {
        request.upload.addEventListener("progress", (event) => {
          const progress = (event.loaded / event.total) * 100
          this.updateProgress(Math.round(progress))
        })
      }
    })

    upload.create((error, blob) => {
      if (error) {
        console.error("[DirectUpload] Error:", error)
        this.showError("Upload failed. Please try again.")
        this.enableFormSubmit()
      } else {
        console.log("[DirectUpload] Success! Blob:", blob)
        this.updateProgress(100)
        
        // Create a hidden field with the signed blob ID
        this.createHiddenBlobField(blob)
        
        // Show completion briefly, then auto-save
        this.showSavingState()
        
        setTimeout(() => {
          this.autoSaveForm()
        }, 500)
      }
    })
  }

  createHiddenBlobField(blob) {
    // Get the input name and create the hidden field name
    const inputName = this.inputTarget.name
    const hiddenFieldName = inputName // Same name as the file input
    
    // Remove any existing hidden field for this attachment
    const existingHidden = this.element.querySelector(`input[type="hidden"][name="${hiddenFieldName}"]`)
    if (existingHidden) {
      existingHidden.remove()
    }
    
    // Create hidden field with the signed blob ID
    const hiddenField = document.createElement("input")
    hiddenField.type = "hidden"
    hiddenField.name = hiddenFieldName
    hiddenField.value = blob.signed_id
    
    // Insert after the file input
    this.inputTarget.insertAdjacentElement("afterend", hiddenField)
    
    console.log(`[DirectUpload] Created hidden field: ${hiddenFieldName} = ${blob.signed_id}`)
  }

  showUploadUI(file) {
    if (this.hasPreviewTarget) {
      const dimensions = this.dimensionsValue || "optimized"
      
      this.previewTarget.innerHTML = `
        <div class="direct-upload-container bg-light border rounded p-4" style="min-height: 180px;">
          <div class="text-center mb-3">
            <i class="bi bi-cloud-arrow-up text-primary" style="font-size: 2.5rem;"></i>
          </div>
          
          <div class="mb-2">
            <div class="d-flex justify-content-between align-items-center mb-1">
              <small class="text-muted fw-medium text-truncate" style="max-width: 200px;">${file.name}</small>
              <small class="text-primary fw-bold upload-percent">0%</small>
            </div>
            <div class="progress" style="height: 12px;">
              <div class="progress-bar bg-primary progress-bar-striped progress-bar-animated" 
                   role="progressbar" 
                   style="width: 0%; transition: width 0.15s ease-out;" 
                   aria-valuenow="0" 
                   aria-valuemin="0" 
                   aria-valuemax="100"></div>
            </div>
          </div>
          
          <div class="text-center">
            <small class="text-muted upload-status">Uploading to S3...</small>
          </div>
        </div>
      `
    }
  }

  updateProgress(percent) {
    // Update the progress bar in our custom UI
    const progressBar = this.element.querySelector('.progress-bar')
    const percentText = this.element.querySelector('.upload-percent')
    const statusText = this.element.querySelector('.upload-status')
    
    if (progressBar) {
      progressBar.style.width = `${percent}%`
      progressBar.setAttribute('aria-valuenow', percent)
    }
    
    if (percentText) {
      percentText.textContent = `${percent}%`
    }
    
    if (statusText) {
      if (percent === 0) {
        statusText.textContent = 'Starting upload...'
      } else if (percent < 100) {
        statusText.textContent = 'Uploading to S3...'
      } else {
        statusText.textContent = 'Upload complete!'
      }
    }
  }

  showError(message) {
    if (this.hasPreviewTarget) {
      this.previewTarget.innerHTML = `
        <div class="bg-danger bg-opacity-10 border border-danger rounded p-4 text-center" style="min-height: 180px;">
          <i class="bi bi-exclamation-triangle-fill text-danger mb-2" style="font-size: 2.5rem;"></i>
          <p class="text-danger fw-bold mb-1">${message}</p>
          <small class="text-muted">Please select a different image and try again.</small>
        </div>
      `
    }
  }

  showSavingState() {
    if (this.hasPreviewTarget) {
      const dimensions = this.dimensionsValue || "optimized"
      
      this.previewTarget.innerHTML = `
        <div class="bg-success bg-opacity-10 border border-success rounded p-4 text-center" style="min-height: 180px;">
          <div class="spinner-border text-success mb-2" role="status">
            <span class="visually-hidden">Saving...</span>
          </div>
          <p class="text-success fw-bold mb-1">✓ Upload Complete!</p>
          <small class="text-muted d-block">Saving and processing image (${dimensions})...</small>
        </div>
      `
    }
  }

  autoSaveForm() {
    // If a dedicated save URL is configured, send only the image via fetch
    // instead of submitting the entire form (which can fail when unrelated
    // fields like admin-only contributor_id or excerpt trigger validation).
    if (this.saveUrlValue && this.fieldNameValue) {
      this.saveImageOnly()
      return
    }

    // Fallback: submit the whole form (legacy behavior)
    const form = this.element.closest("form")
    if (form) {
      console.log("[DirectUpload] Auto-saving form...")

      // Re-enable submit buttons before submitting
      const submitButtons = form.querySelectorAll('[data-was-disabled="true"]')
      submitButtons.forEach(btn => {
        btn.disabled = false
        delete btn.dataset.wasDisabled
      })

      // Submit the form
      form.requestSubmit()
    }
  }

  saveImageOnly() {
    const hiddenField = this.element.querySelector(`input[type="hidden"][name="${this.inputTarget.name}"]`)
    if (!hiddenField) {
      console.error("[DirectUpload] No hidden blob field found")
      this.showError("Upload succeeded but save failed. Please try again.")
      this.enableFormSubmit()
      return
    }

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
    const formData = new FormData()
    formData.append(this.fieldNameValue, hiddenField.value)

    console.log(`[DirectUpload] Saving image via ${this.saveUrlValue}`)

    fetch(this.saveUrlValue, {
      method: "PATCH",
      headers: {
        "X-CSRF-Token": csrfToken,
        "Accept": "text/html, application/json"
      },
      body: formData
    }).then(response => {
      if (response.ok) {
        const contentType = response.headers.get("content-type") || ""

        if (contentType.includes("text/html")) {
          // HTML response — replace the Turbo Frame in-place
          return response.text().then(html => {
            console.log("[DirectUpload] Replacing image frame with server response")
            // Re-enable form buttons before replacing the frame
            this.enableFormSubmit()
            const turboFrame = this.element.closest("turbo-frame")
            if (turboFrame) {
              turboFrame.outerHTML = html
            } else {
              // Fallback: no turbo frame wrapper, reload page
              console.log("[DirectUpload] No turbo-frame found, reloading page")
              window.location.reload()
            }
          })
        } else {
          // JSON / empty response (legacy) — reload page
          console.log("[DirectUpload] Image saved successfully (non-HTML response)")
          window.location.reload()
        }
      } else {
        return response.json().then(data => {
          const msg = (data.errors || [data.error]).join(", ") || "Save failed"
          console.error("[DirectUpload] Save error:", msg)
          this.showError(msg)
          this.enableFormSubmit()
        })
      }
    }).catch(err => {
      console.error("[DirectUpload] Network error:", err)
      this.showError("Network error while saving. Please try again.")
      this.enableFormSubmit()
    })
  }

  disableFormSubmit() {
    const form = this.element.closest("form")
    if (form) {
      const submitButtons = form.querySelectorAll('input[type="submit"], button[type="submit"], button:not([type])')
      submitButtons.forEach(btn => {
        btn.disabled = true
        btn.dataset.originalText = btn.textContent || btn.value
        btn.dataset.wasDisabled = 'true'
        if (btn.tagName === "BUTTON") {
          btn.innerHTML = `<span class="spinner-border spinner-border-sm me-1"></span> Uploading...`
        } else {
          btn.value = "Uploading..."
        }
      })
    }
  }

  enableFormSubmit() {
    const form = this.element.closest("form")
    if (form) {
      const submitButtons = form.querySelectorAll('[data-was-disabled="true"]')
      submitButtons.forEach(btn => {
        btn.disabled = false
        delete btn.dataset.wasDisabled
        if (btn.dataset.originalText) {
          if (btn.tagName === "BUTTON") {
            btn.textContent = btn.dataset.originalText
          } else {
            btn.value = btn.dataset.originalText
          }
          delete btn.dataset.originalText
        }
      })
    }
  }

  formatFileSize(bytes) {
    if (bytes < 1024) return bytes + ' B'
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB'
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB'
  }
}
